import './sidebar.scss';
import '@/shared/ui/button/button.scss';

import { LogoutOutlined, PhoneOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import NavContractsIcon from '@/media/icons/clarity-contract.svg?react';
import CloseIcon from '@/media/icons/close.svg?react';
import NavAppealsIcon from '@/media/icons/nav-appeals.svg?react';
import NavOverviewIcon from '@/media/icons/nav-overview.svg?react';
import NavRequestsIcon from '@/media/icons/nav-requests.svg?react';
import NavServicesIcon from '@/media/icons/nav-services.svg?react';
import PlusIcon from '@/media/icons/plus.svg?react';
import logo from '@/media/logo.svg';
import { SUPPORT_PHONE } from '@/shared/constants';
import { ROUTES } from '@/shared/routes';
import { LocalizedLink, LocalizedNavLink } from '@/shared/ui';

import useHeaderUserMenu from '../header/hooks/useHeaderUserMenu';
import { NAV, NAV_ROUTES } from './constants';

const NAV_ICONS = {
  overview: NavOverviewIcon,
  requests: NavRequestsIcon,
  contracts: NavContractsIcon,
  appeals: NavAppealsIcon,
  services: NavServicesIcon
} as const;

interface IProps {
  isMobileMenuOpen: boolean;
  onMobileMenuClose: () => void;
}

const Sidebar: React.FC<IProps> = ({ isMobileMenuOpen, onMobileMenuClose }) => {
  const { t } = useTranslation();

  const { userName, profileInitial, handleLogout } = useHeaderUserMenu();

  const renderNavItem = (
    navItem: (typeof NAV)[number],
    onNavigate?: () => void
  ) => {
    const NavIcon = NAV_ICONS[navItem.id];

    return (
      <LocalizedNavLink
        key={navItem.id}
        to={NAV_ROUTES[navItem.id]}
        end={navItem.id === 'overview'}
        onClick={onNavigate}
        className={({ isActive }) =>
          classNames('sidebar__nav-item', {
            'sidebar__nav-item--active': isActive
          })
        }
      >
        <NavIcon className="sidebar__nav-icon" aria-hidden />
        <span className="sidebar__nav-label">{t(navItem.label)}</span>
      </LocalizedNavLink>
    );
  };

  return (
    <>
      <button
        type="button"
        className={classNames('sidebar__overlay', {
          'sidebar__overlay--visible': isMobileMenuOpen
        })}
        aria-label={t('layout.sidebar.closeMenu')}
        onClick={onMobileMenuClose}
      />

      <aside
        className={classNames('sidebar', {
          'sidebar--open': isMobileMenuOpen
        })}
      >
        <div className="sidebar__brand-header">
          <LocalizedLink
            className="sidebar__brand"
            to={ROUTES.leasing}
            onClick={onMobileMenuClose}
          >
            <img src={logo} alt="Nomad" className="sidebar__brand-logo" />
          </LocalizedLink>

          <button
            type="button"
            className="sidebar__close"
            aria-label={t('layout.sidebar.closeMenu')}
            onClick={onMobileMenuClose}
          >
            <CloseIcon className="sidebar__close-icon" aria-hidden />
          </button>
        </div>

        <div className="sidebar__new-request">
          <LocalizedLink
            className="button button--filled-purple button--full-width sidebar__new-request-link"
            to={ROUTES.leasingRequestNew}
            onClick={onMobileMenuClose}
          >
            <PlusIcon className="sidebar__new-request-icon" />
            {t('layout.header.newRequestTitle')}
          </LocalizedLink>
        </div>

        <nav className="sidebar__nav">
          {NAV.map((navItem) => renderNavItem(navItem, onMobileMenuClose))}
        </nav>

        <div className="sidebar__manager">
          <div className="sidebar__manager-card">
            <div className="sidebar__manager-info">
              <div className="sidebar__manager-avatar">
                <PhoneOutlined className="sidebar__manager-phone-icon" />
              </div>
              <div className="sidebar__manager-details">
                <div className="sidebar__manager-name">
                  {t('layout.support.title')}
                </div>
              </div>
            </div>
            <div className="sidebar__manager-action">
              <a
                href={`tel:${SUPPORT_PHONE}`}
                className="sidebar__manager-button"
              >
                {t('layout.support.call')}
              </a>
            </div>
          </div>
        </div>

        <div className="sidebar__logout">
          <div
            className="sidebar__logout-avatar"
            aria-label={userName || t('layout.userMenu.profileAria')}
          >
            {profileInitial}
          </div>
          <button
            type="button"
            className="sidebar__logout-button"
            onClick={handleLogout}
          >
            <LogoutOutlined className="sidebar__logout-icon" aria-hidden />
            {t('layout.userMenu.logout')}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
